/**
 * EditFileSection Component
 * UI for replacing a file in an existing document (edit mode)
 * Allows optional file replacement
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as InsertDriveFileIcon
} from '@mui/icons-material';
import { EditFileSectionProps } from '../types';

export const EditFileSection: React.FC<EditFileSectionProps> = ({
  selectedFile,
  onFileSelect,
  onBrowse,
  onRemove,
  fileInputRef
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Typography variant="subtitle1" sx={{ mt: 3, mb: 2, fontWeight: 600 }}>
        {t('documents.dialog.replaceFile')}
      </Typography>
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('documents.dialog.optionalFileReplace')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Hidden file input */}
          <input
            type="file"
            hidden
            accept=".pdf,.docx,.pptx,.txt"
            ref={fileInputRef}
            onChange={onFileSelect}
          />

          {/* No file selected */}
          {!selectedFile ? (
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={onBrowse}
              >
                {t('documents.dialog.selectNewFile')}
              </Button>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                {t('documents.dialog.keepCurrentFile')}
              </Typography>
            </Box>
          ) : (
            /* File selected */
            <Box sx={{ width: '100%', p: 2 }}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <InsertDriveFileIcon sx={{ mr: 1 }} />
                    <Typography variant="body1" sx={{ flexGrow: 1 }}>
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
              <Button
                variant="outlined"
                size="small"
                onClick={onRemove}
              >
                {t('documents.dialog.removeNewFile')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};
